import React, { useState, useEffect } from "react";
import axios from "axios";
import styles from "./allAssessments.module.css";

/* ================= API HELPERS ================= */

const fetchCompanyData = async () => {
  const response = await fetch(
    "https://casaahaanahotels.com/search_company_data",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ useremail: "" }),
    }
  );
  return await response.json();
};

const fetchDepartmentData = async () => {
  const response = await axios.post(
    "https://casaahaanahotels.com/get_master_assessment_type",
    {}
  );
  return response.data || [];
};

/* ================= COMPONENT ================= */

const AssessmentDashboard = () => {
  const [allAssessments, setAllAssessments] = useState([]); // 🔥 FULL DATA
  const [assessments, setAssessments] = useState([]);       // 🔥 FILTERED DATA

  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [searchType, setSearchType] = useState("company");
  const [selectedCompanyCode, setSelectedCompanyCode] = useState("");
  const [selectedDepartmentCode, setSelectedDepartmentCode] = useState("");
  const [searchUserEmail, setSearchUserEmail] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const itemsPerPage = 10;

  /* ================= INITIAL LOAD ================= */

  useEffect(() => {
    loadAssessments(); // 🔥 EMPTY PAYLOAD ONLY
    fetchCompanyData().then(setCompanies);
    fetchDepartmentData().then(setDepartments);
  }, []);

  /* ================= LOAD ALL ASSESSMENTS ================= */

  const loadAssessments = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        "https://casaahaanahotels.com/api/v2/assessment_scores_enriched",
        {}
      );

      const data = response.data?.data || [];
      setAllAssessments(data);
      setAssessments(data); // first time show all
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  };

  /* ================= LOCAL SEARCH (NO API) ================= */

  const handleSearch = () => {
    setLoading(true);

    let filtered = [...allAssessments];

    if (searchType === "company" && selectedCompanyCode) {
      filtered = filtered.filter(
        (a) => a.companycode === selectedCompanyCode
      );
    }

    if (searchType === "department" && selectedDepartmentCode) {
      filtered = filtered.filter(
        (a) => a.departmentcode === selectedDepartmentCode
      );
    }

    if (searchType === "email" && searchUserEmail.trim()) {
      filtered = filtered.filter((a) =>
        a.driver_email
          ?.toLowerCase()
          .includes(searchUserEmail.trim().toLowerCase())
      );
    }

    setAssessments(filtered);
    setCurrentPage(1);
    setLoading(false);
  };

  /* ================= PAGINATION ================= */

  const totalPages = Math.ceil(assessments.length / itemsPerPage);
  const paginated = assessments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getPagination = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) pages.push(1, 2, 3, 4, "...", totalPages);
      else if (currentPage >= totalPages - 2)
        pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      else
        pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
    }
    return pages;
  };

  /* ================= SCORE IMAGE ================= */

  const getScoreImage = (score) => {
    if (!score) return 1;
    return score; // API already sends 1–5
  };

  /* ================= UI ================= */

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Assessment Dashboard</h2>

      <div className={styles.filterContainer}>
        <select
          className={styles.companyDropdown}
          value={searchType}
          onChange={(e) => setSearchType(e.target.value)}
        >
          <option value="company">Search by Company</option>
          <option value="department">Search by Department</option>
          <option value="email">Search by Email</option>
        </select>

        {searchType === "company" && (
          <select
            className={styles.companyDropdown}
            value={selectedCompanyCode}
            onChange={(e) => setSelectedCompanyCode(e.target.value)}
          >
            <option value="">Select Company</option>
            {companies.map((c) => (
              <option key={c.id} value={c.admincode}>
                {c.companyname}
              </option>
            ))}
          </select>
        )}

        {searchType === "department" && (
          <select
            className={styles.companyDropdown}
            value={selectedDepartmentCode}
            onChange={(e) => setSelectedDepartmentCode(e.target.value)}
          >
            <option value="">Select Department</option>
            {departments.map((d) => (
              <option key={d.assessment_type_id} value={d.assessment_type_id}>
                {d.AssessmentType}
              </option>
            ))}
          </select>
        )}

        {searchType === "email" && (
          <input
            className={styles.companyDropdown}
            placeholder="Enter Email"
            value={searchUserEmail}
            onChange={(e) => setSearchUserEmail(e.target.value)}
          />
        )}

        <button
          className={styles.searchButton}
          onClick={handleSearch}
          disabled={loading}
        >
          {loading ? "Searching..." : "Search"}
        </button>
      </div>

      <table className={styles.assessmentTable}>
        <thead>
          <tr>
            <th>Driver</th>
            <th>Email</th>
            <th>Company</th>
            <th>Department</th>
            <th>Score</th>
            <th>Validity</th>
            <th>Last Updated</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan="7" className={styles.loadingRow}>
                Loading data...
              </td>
            </tr>
          ) : paginated.length === 0 ? (
            <tr>
              <td colSpan="7" className={styles.loadingRow}>
                No assessments have been created yet!
              </td>
            </tr>
          ) : (
            paginated.map((a, i) => (
              <tr key={i}>
                <td>{a.driver_name}</td>
                <td>{a.driver_email}</td>
                <td>{a.companyname}</td>
                <td>{a.departmentName}</td>
                <td>
                  <img
                    className={styles.scoreImage}
                    src={`https://starbehaviors.com/uploadimage/${getScoreImage(a.score)}.png`}
                    alt="Score"
                  />
                </td>
                <td>{new Date(a.validity).toLocaleDateString()}</td>
                <td>{new Date(a.score_timestamp).toLocaleDateString()}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className={styles.pagination}  style={{ marginTop: '16px', display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className={styles.pageButton}
          >
            Prev
          </button>

          {getPagination().map((p, i) => (
            <button
              key={i}
              disabled={p === "..."}
              onClick={() => p !== "..." && setCurrentPage(p)}
              className={p === currentPage ? styles.activePageButton : styles.pageButton}
              style={{ minWidth: 32 }}
            >
              {p}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={styles.pageButton}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AssessmentDashboard;
